import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FaceIdentityBrandLogo from '../face-identity/components/FaceIdentityBrandLogo';
import { useFaceIdentityOrgLogo } from '../face-identity/hooks/useFaceIdentityOrgLogo';
import { useKioskLandingConfig } from '../face-identity/hooks/useKioskLandingConfig';
import {
  KIOSK_PAGE_KEYS,
  buildLandingRootStyle,
  buildSizingStyle,
  isSectionVisible,
} from '../face-identity/utils/kioskLandingDefaults';
import { hairAppPath } from '../../hair-analyzer/src/hair/config/hairApp';
import { nyafazReelsPath } from '../../nyafaz_reels/paths';
import { SALON_SERVICE_LOBBY_PATH } from '../salon/config/salonApp';
import { bootstrapFaceSalonToCapture } from '../salon/utils/bootstrapFaceSalonSession';
import { captureDeviceInfo } from '../salon/utils/deviceCapture';
import { useSkinSession } from '../salon/context/SkinSessionContext';
import {
  logoutFranchiseFlow,
  validateFranchiseFlowAuth,
} from '../shared/franchiseFlowAuth';
import { SHELL_MQ } from '../shared/tabletMedia';
import { resetNaturalsSalonLuxScroll } from '../shared/resetEnterpriseSalonLuxScroll';
import { prefetchDeviceLocation } from '../shared/deviceLocationCache';
import {
  PRESENTATION_CUSTOMER_ENGAGE_URL,
  PRESENTATION_HUB_PATH,
  PRESENTATION_LOGIN_PATH,
  PRESENTATION_NATURALS_HAIR_ANALYSIS_HOME,
  PRESENTATION_NATURALS_HAIR_COLOR_CAPTURE,
  PRESENTATION_NATURALS_HAIR_STYLE_CAPTURE,
  PRESENTATION_NATURALS_MAKEUP_CAPTURE,
  presentationHubBackgroundStyle,
} from './presentationConfig';
import {
  buildPresentationNavState,
  ensurePresentationGuestCheckin,
  ensurePresentationReelsCheckin,
  isPresentationFranchiseSession,
  openPresentationPassportScan,
  presentationCheckinForReels,
  setPresentationMode,
} from './presentationSession';
import { warmSalonFaceEngine } from '../face-identity/utils/faceIdentityApi';
import '../salon/pages/FlowSelectPage.css';
import './PresentationHubPage.css';

/**
 * Screen designs the hub can show. The kiosk picks one from the dropdown in the header (confirmed in a
 * popup); the choice is remembered on this device.
 *   naturals  the custom luxury Naturals AI Suite presentation UI (default)
 *   original  the original dynamic kiosk landing config & theme
 *   nyafaz    the Nyafaz glass UI: same tiles and actions as Naturals, pink and teal on frosted glass
 */
const PRESENTATION_DESIGNS = [
  { id: 'naturals', label: 'Naturals' },
  { id: 'original', label: 'Original' },
  { id: 'nyafaz', label: 'Nyafaz' },
];
const DEFAULT_PRESENTATION_DESIGN = 'naturals';
const PRESENTATION_DESIGN_STORAGE_KEY = 'presentation-hub-design';
const NYAFAZ_HUB_BACKGROUND = `url("${encodeURI(`${process.env.PUBLIC_URL || ''}/naturals/presentation-bg-nyafaz-mirror-spa.png`)}")`;
// Portrait version for tablets and phones (<=1024px, swapped in by PresentationHubPage.css).
const NYAFAZ_HUB_BACKGROUND_TABLET = `url("${encodeURI(`${process.env.PUBLIC_URL || ''}/naturals/presentation-bg-nyafaz-tablet.png`)}")`;

function readStoredDesign() {
  try {
    const saved = window.localStorage.getItem(PRESENTATION_DESIGN_STORAGE_KEY);
    return PRESENTATION_DESIGNS.some((d) => d.id === saved) ? saved : DEFAULT_PRESENTATION_DESIGN;
  } catch {
    return DEFAULT_PRESENTATION_DESIGN;
  }
}

function storeDesign(id) {
  try {
    window.localStorage.setItem(PRESENTATION_DESIGN_STORAGE_KEY, id);
  } catch {
    // Private mode or blocked storage: the design still switches for this visit.
  }
}

/** AI WhatsApp Chat tile opens a chat with this number (country code + number, digits only). */
const AI_WHATSAPP_NUMBER = '917358140342';
const AI_WHATSAPP_URL = `https://wa.me/${AI_WHATSAPP_NUMBER}?text=${encodeURIComponent('Hi')}`;

/** Booking tile opens walk-in appointment booking in the Customer Engagement (demo engagement) app. */
const WALK_IN_BOOKING_URL = `${(PRESENTATION_CUSTOMER_ENGAGE_URL || '/naturals/engagement').replace(/\/+$/, '')}/appointments?book=walk_in`;

/** AI Voice Call tile opens the call screen in the Customer Engagement (demo engagement) app. */
const AI_VOICE_CALL_URL = `${(PRESENTATION_CUSTOMER_ENGAGE_URL || '/naturals/engagement').replace(/\/+$/, '')}/voice-call`;

/**
 * Nyafaz design only: Customer Engagement, Booking and AI Voice Call open the Nyafaz engagement app
 * (same backend, Nyafaz branding). Naturals and Original keep the links above.
 */
const NYAFAZ_ENGAGE_URL =
  (process.env.REACT_APP_NYAFAZ_ENGAGEMENT_APP_URL || '').trim().replace(/\/+$/, '') || '/naturals/nyafaz-engagement';
const NYAFAZ_WALK_IN_BOOKING_URL = `${NYAFAZ_ENGAGE_URL}/appointments?book=walk_in`;
const NYAFAZ_AI_VOICE_CALL_URL = `${NYAFAZ_ENGAGE_URL}/voice-call`;

/** POS tile opens the demo POS (billing) app in a new tab. */
const DEMO_POS_URL = (process.env.REACT_APP_DEMO_POS_APP_URL || '').trim() || '/naturals/demo-pos';

// ── Detailed Luxury Gold Line-Art Icons for Naturals Presentation ───────────

const PassportCardIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="7" y="6" width="34" height="36" rx="6" />
    <rect x="13" y="11" width="22" height="18" rx="3" strokeDasharray="32" strokeWidth="1.2" opacity="0.65" />
    <circle cx="24" cy="18" r="4.5" />
    <path d="M16 27c0-3.5 3.5-5 8-5s8 1.5 8 5" />
    <path d="M14 34h20M14 38h13" strokeWidth="1.6" />
  </svg>
);

const SkinAnalysisIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 8c-6 0-11 4.5-11 11 0 3 1.2 5.5 1.8 7.8.5 1.8.3 3-.8 4.2-1.3 1.4-1 3.8.8 5 1.6 1 3.8.5 5-.7l2-1.8c1.6.6 3.5.6 5.2 0" />
    <path d="M16 19c1.8.6 3.6 0 4.8-1.2" strokeWidth="1.5" />
    <path d="M21 8c3.5 1 5.5 3 6.5 6.5.6 2 .4 3.5-.8 4.5" strokeWidth="1.4" opacity="0.75" />
    <path d="M34 22v8m-4-4h8" strokeWidth="1.9" />
    <circle cx="34" cy="26" r="0.75" fill="currentColor" />
    <path d="M38 12v5m-2.5-2.5h5" strokeWidth="1.6" />
    <circle cx="38" cy="14.5" r="0.6" fill="currentColor" />
    <path d="M29 36l1.5-1.5 1.5 1.5-1.5 1.5z" fill="currentColor" stroke="none" />
  </svg>
);

const HairAnalysisIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15c-.8 2.8 0 5.2 1.5 7.2 1 1.4 1.2 2.5.6 4-1.2 3.2-.6 5.4 1.2 6.8" />
    <path d="M19 22c1.2.4 2.4 0 3.2-.8" strokeWidth="1.4" />
    <path d="M18 12c4-5 10.5-5.5 15.5-2.5 5 3 6.5 10 2.5 15.5-4 5.5-2.5 10.5 0 14.5" />
    <path d="M14 16c2.5-2.5 6.5-3 10-1.2 4.5 2.2 5 7.5 1.8 12-3.2 4.5-2 9 .6 12" />
    <path d="M25 19c2-1.2 5-1 7 .6 2.5 2.2 2.8 6 .6 9.5-2.2 3.5-1.2 7 .6 9.5" />
    <path d="M29 23c1.5 1 3 3 2 5.5-1 2.5 0 4.5 1.2 6" strokeWidth="1.4" opacity="0.7" />
  </svg>
);

const MakeupIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="8" y="23" width="10" height="18" rx="2" />
    <rect x="10" y="17" width="6" height="6" />
    <path d="M10 17v-6l6-4.5v10.5" />
    <path d="M12 12l4-3" strokeWidth="1.2" opacity="0.6" />
    <circle cx="32" cy="28" r="10" />
    <ellipse cx="32" cy="28" rx="6.5" ry="5.8" strokeDasharray="2 2" strokeWidth="1.2" opacity="0.75" />
    <circle cx="32" cy="28" r="3" strokeWidth="1.2" />
    <path d="M26 14l3-3 2 2-3 3z" strokeWidth="1.4" />
    <path d="M37 12l2-2m-1 3l2-2" strokeWidth="1.4" />
  </svg>
);

const HairColoringIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M16 32c-3.2-2.5-5.2-7-5.2-11.5 0-8.5 7-15.5 15.5-15.5 5.8 0 10.8 3.2 13.4 8" />
    <path d="M18 21c2.5-4 6.5-6 11.5-5.2" />
    <path d="M19 27c3.8 2 7.6 1.2 10.2-1.2" />
    <path d="M14 35c3.8 5 10.2 7.5 16.5 6.2" />
    <path d="M36 10l5-5m-1 9l5-5" strokeWidth="1.5" />
    <path d="M30 19l8-8 4 4-8 8-4-4z" />
  </svg>
);

const HairStyleIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 13l2.5-5 4 3.5 4-3.5 2.5 5z" strokeWidth="1.9" />
    <circle cx="26.5" cy="7.5" r="1" fill="currentColor" />
    <circle cx="22.5" cy="11" r="0.8" fill="currentColor" />
    <circle cx="30.5" cy="11" r="0.8" fill="currentColor" />
    <path d="M18 18c-3.2 2-5 5.8-5 10.2 0 6.5 4.5 11.5 10.2 12.2" />
    <path d="M35 18c3.2 2 5 5.8 5 10.2 0 7.8-5.8 13.5-13.5 13.5" />
    <path d="M26.5 20c-2.5 2.5-2.5 6.5-1.8 9.8.5 2.2 2 4.5 2 6.5" />
    <path d="M17 25c2.5 2.5 5 1.2 7.5 0" strokeWidth="1.4" opacity="0.75" />
    <path d="M30 26c2 2 4 2 6 0" strokeWidth="1.4" opacity="0.75" />
  </svg>
);

const ReelsCameraIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="8" y="14" width="32" height="24" rx="5" />
    <path d="M17 14V9h14v5" strokeWidth="1.9" />
    <circle cx="19" cy="11.5" r="1.5" fill="currentColor" />
    <circle cx="29" cy="11.5" r="1.5" fill="currentColor" />
    <path d="M40 21l4-2.5v11l-4-2.5" strokeWidth="1.6" />
    <polygon points="21,21 30,26 21,31" fill="currentColor" stroke="none" />
  </svg>
);

const CustomerEngageCardIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 13a4 4 0 014-4h18a4 4 0 014 4v12a4 4 0 01-4 4h-8l-7 6v-6h-3a4 4 0 01-4-4V13z" />
    <path d="M35 18h2a3 3 0 013 3v10a3 3 0 01-3 3h-2v4l-5-4h-3" strokeWidth="1.5" opacity="0.75" />
    <circle cx="17" cy="19" r="1.5" fill="currentColor" />
    <circle cx="22" cy="19" r="1.5" fill="currentColor" />
    <circle cx="27" cy="19" r="1.5" fill="currentColor" />
  </svg>
);

const AIWhatsAppIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M24 8C15.2 8 8 15.2 8 24c0 3.2 1 6.1 2.6 8.6L9 40l7.8-1.6C19.1 39.4 21.5 40 24 40c8.8 0 16-7.2 16-16S32.8 8 24 8z" />
    <path d="M18 24h.02M24 24h.02M30 24h.02" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const AIVoiceCallIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 9a2 2 0 00-2 2c0 14.36 11.64 26 26 26a2 2 0 002-2v-5a2 2 0 00-1.6-1.96l-5.4-1.08a2 2 0 00-2.06.74l-2.2 2.69a19.2 19.2 0 01-8.39-8.39l2.7-2.2a2 2 0 00.74-2.06L22.7 7.34A2 2 0 0020.74 5.74L16 6a2 2 0 00-2 2z" />
    <path d="M32 12c3.5 2 6 5.5 6 9.5" strokeWidth="1.8" />
    <path d="M37 7c6 3.5 10 9.5 10 16" strokeWidth="1.8" />
  </svg>
);

const BookingIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="8" y="11" width="32" height="29" rx="4" />
    <path d="M8 19h32" />
    <path d="M16 7v8M32 7v8" />
    <path d="M18 29l4 4 8-8" strokeWidth="2.1" />
    <circle cx="40" cy="9" r="0.75" fill="currentColor" />
  </svg>
);

const PosIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="9" y="7" width="22" height="34" rx="3" />
    <path d="M14 14h12M14 20h12M14 26h7" strokeWidth="1.6" />
    <path d="M14 33h12" strokeDasharray="2 2" strokeWidth="1.4" opacity="0.75" />
    <rect x="31" y="20" width="10" height="16" rx="2" />
    <path d="M36 24v8m-2.5-6.5c0-1 1-1.5 2.5-1.5s2.5.6 2.5 1.6c0 2.4-5 1.4-5 3.8 0 1 1 1.6 2.5 1.6s2.5-.5 2.5-1.5" strokeWidth="1.4" />
    <circle cx="40" cy="10" r="0.75" fill="currentColor" />
  </svg>
);

const ProductRecommenderIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="14" y="19" width="16" height="22" rx="4" />
    <path d="M18 19v-5h8v5" />
    <path d="M22 14V9h-6" strokeWidth="1.9" />
    <circle cx="22" cy="30" r="3" strokeWidth="1.4" opacity="0.8" />
    <path d="M38 12v6m-3-3h6" strokeWidth="1.8" />
    <circle cx="38" cy="15" r="0.75" fill="currentColor" />
    <path d="M35 27v4m-2-2h4" strokeWidth="1.5" />
    <circle cx="35" cy="29" r="0.6" fill="currentColor" />
  </svg>
);

const CircleArrowIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M8 5l5 5-5 5" />
  </svg>
);

const HomeNavIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 10.5L12 3l9 7.5" />
    <path d="M5 9v11a1 1 0 001 1h12a1 1 0 001-1V9" />
    <path d="M10 21v-6h4v6" />
  </svg>
);

const AccessibilityFigureIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="5" r="2.2" />
    <path d="M5 9h14M12 9v11M8 20l4-6 4 6" />
  </svg>
);

const GlobeNavIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M3.6 9h16.8M3.6 15h16.8M12 3a14 14 0 010 18M12 3a14 14 0 000 18" />
  </svg>
);

const SignOutNavIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
  </svg>
);

const RefreshNavIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 11a8 8 0 10-2.3 5.7M20 4v7h-7" />
  </svg>
);

const PaletteNavIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3a9 9 0 100 18c1.1 0 1.8-.8 1.8-1.8 0-.5-.2-.9-.5-1.2-.3-.3-.5-.8-.5-1.2 0-1 .8-1.8 1.8-1.8H17a4 4 0 004-4c0-4.4-4-8-9-8z" />
    <circle cx="7.5" cy="11" r="1.1" fill="currentColor" stroke="none" />
    <circle cx="10.5" cy="7.2" r="1.1" fill="currentColor" stroke="none" />
    <circle cx="15" cy="7.4" r="1.1" fill="currentColor" stroke="none" />
  </svg>
);

/**
 * Header design button: a round palette icon with the native design list laid invisibly over it, so a tap
 * opens the list. Picking a different design asks for confirmation before the screen changes.
 */
function DesignSwitcher({ value, onPick }) {
  const current = PRESENTATION_DESIGNS.find((d) => d.id === value);
  return (
    <label className="pres-design-switch" title={`Screen design: ${current?.label || ''}`}>
      <PaletteNavIcon />
      <select
        className="pres-design-switch-select"
        value={value}
        onChange={(e) => onPick(e.target.value)}
        aria-label={`Screen design: ${current?.label || ''}`}
      >
        {PRESENTATION_DESIGNS.map((d) => (
          <option key={d.id} value={d.id}>
            {d.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function DesignConfirmDialog({ design, onCancel, onConfirm }) {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onCancel();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className="pres-design-modal-overlay" onClick={onCancel} role="presentation">
      <div
        className={`pres-design-modal pres-design-modal--${design.id}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pres-design-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <p id="pres-design-modal-title" className="pres-design-modal-title">
          Switch to the {design.label} screen?
        </p>
        <p className="pres-design-modal-text">
          The presentation screen will change to the {design.label} design on this device.
        </p>
        <div className="pres-design-modal-actions">
          <button type="button" className="pres-design-modal-btn pres-design-modal-btn--cancel" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="pres-design-modal-btn pres-design-modal-btn--ok" onClick={onConfirm} autoFocus>
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Original fallback icons ──────────────────────────────────────────────────

const LegacyPassportIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" width="40" height="40">
    <rect x="4" y="3" width="16" height="18" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="12" cy="10" r="3" fill="none" stroke="currentColor" strokeWidth="1.4" />
    <path d="M7 17c1.2-2 3-3 5-3s3.8 1 5 3" fill="none" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

const LegacyReelsIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" width="40" height="40">
    <rect x="3" y="5" width="14" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
    <path d="M17 9l4-2v10l-4-2" fill="none" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const LegacyCustomerEngageIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" width="40" height="40">
    <path d="M4 5h12a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H9l-4 3v-3H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M20 9h0a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-1v3l-4-3h-3" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    <circle cx="6.5" cy="10" r="0.9" fill="currentColor" />
    <circle cx="10" cy="10" r="0.9" fill="currentColor" />
    <circle cx="13.5" cy="10" r="0.9" fill="currentColor" />
  </svg>
);

const LEGACY_TILES = [
  {
    id: 'passport',
    title: 'Digital Check-in',
    icon: 'passport-svg',
    action: 'passport',
  },
  {
    id: 'skin',
    title: 'Skin Analysis',
    iconSrc: '/hub/skin.png',
    action: 'skin',
  },
  {
    id: 'hair-analyze',
    title: 'Hair Analysis',
    iconSrc: '/hub/hair.png',
    path: hairAppPath('/'),
  },
  {
    id: 'makeup',
    title: 'Makeup',
    iconSrc: '/hub/makeup.png',
    path: PRESENTATION_NATURALS_MAKEUP_CAPTURE,
  },
  {
    id: 'reels',
    title: 'Reels',
    icon: 'reels-svg',
    action: 'reels',
  },
  ...(PRESENTATION_CUSTOMER_ENGAGE_URL
    ? [
      {
        id: 'customer-engage',
        title: 'Customer Engagement',
        icon: 'engage-svg',
        externalUrl: PRESENTATION_CUSTOMER_ENGAGE_URL,
      },
    ]
    : []),
];

export default function PresentationHubPage() {
  const navigate = useNavigate();
  const { token } = useSkinSession();
  const { orgLogoUrl, showLogo, onLogoError } = useFaceIdentityOrgLogo();
  const { config: landingConfig } = useKioskLandingConfig(KIOSK_PAGE_KEYS.SALON_ENTRY);
  const brand = landingConfig?.brand || {};
  const hubThemeStyle = useMemo(
    () => buildLandingRootStyle(landingConfig || {}),
    [landingConfig],
  );
  const hubSizingStyle = useMemo(
    () => buildSizingStyle(landingConfig?.sizing || {}),
    [landingConfig?.sizing],
  );
  const showBrand = isSectionVisible(landingConfig, 'brand');
  const showBrandLogo = showBrand && brand.showLogo !== false;
  const pageRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [error, setError] = useState('');
  const [design, setDesign] = useState(readStoredDesign);
  const [pendingDesign, setPendingDesign] = useState(null);

  const requestDesign = (id) => {
    if (id !== design) setPendingDesign(id);
  };
  const cancelDesign = useCallback(() => setPendingDesign(null), []);
  const confirmDesign = () => {
    storeDesign(pendingDesign);
    setDesign(pendingDesign);
    setPendingDesign(null);
  };
  const designSwitcher = <DesignSwitcher value={design} onPick={requestDesign} />;
  const designDialog = pendingDesign ? (
    <DesignConfirmDialog
      design={PRESENTATION_DESIGNS.find((d) => d.id === pendingDesign)}
      onCancel={cancelDesign}
      onConfirm={confirmDesign}
    />
  ) : null;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const session = await validateFranchiseFlowAuth();
      if (cancelled) return;
      if (!session || !isPresentationFranchiseSession(session)) {
        await logoutFranchiseFlow();
        navigate(PRESENTATION_LOGIN_PATH, { replace: true });
        return;
      }
      setPresentationMode(true);
      if (!cancelled) setReady(true);
      try {
        await ensurePresentationGuestCheckin();
        if (!cancelled) setError('');
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || 'Could not prepare demo guest. Retry or sign out.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useEffect(() => {
    if (!ready) return undefined;
    void warmSalonFaceEngine('/api/salon/face').catch(() => {});
    return undefined;
  }, [ready]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const mq = window.matchMedia(SHELL_MQ);
    const apply = () => {
      if (mq.matches) {
        html.classList.add('salon-hub-scroll', 'naturals-touch-shell');
        body.classList.add('salon-hub-scroll', 'naturals-touch-shell');
      } else {
        html.classList.remove('salon-hub-scroll', 'naturals-touch-shell');
        body.classList.remove('salon-hub-scroll', 'naturals-touch-shell');
      }
    };
    apply();
    mq.addEventListener('change', apply);
    return () => {
      mq.removeEventListener('change', apply);
      html.classList.remove('salon-hub-scroll', 'naturals-touch-shell');
      body.classList.remove('salon-hub-scroll', 'naturals-touch-shell');
    };
  }, []);

  useEffect(() => {
    resetNaturalsSalonLuxScroll(pageRef.current?.parentElement);
  }, []);

  const navState = useCallback(
    (extra = {}) => buildPresentationNavState(extra),
    [],
  );

  const openPath = (path) => {
    navigate(path, { state: navState() });
  };

  const openBeautyStudio = async (path, busyKey) => {
    if (!ready || busyId) return;
    setBusyId(busyKey);
    setError('');
    try {
      setPresentationMode(true);
      try {
        prefetchDeviceLocation();
      } catch {
        /* ignore */
      }
      await ensurePresentationGuestCheckin();
      openPath(path);
    } catch (e) {
      setError(e?.message || 'Could not open studio.');
    } finally {
      setBusyId('');
    }
  };

  const handleSkin = async () => {
    if (busyId) return;
    setBusyId('skin');
    setError('');
    try {
      await ensurePresentationGuestCheckin();
      const state = navState({ deviceInfo: captureDeviceInfo() });
      await bootstrapFaceSalonToCapture(navigate, {
        token: token || null,
        deviceInfo: state.deviceInfo,
        locationState: state,
        replace: false,
      });
    } catch (e) {
      setError(e?.message || 'Could not start skin analysis.');
    } finally {
      setBusyId('');
    }
  };

  const handleReels = async () => {
    if (busyId) return;
    setBusyId('reels');
    setError('');
    try {
      await ensurePresentationReelsCheckin();
      const checkin = presentationCheckinForReels();
      if (!checkin?.id) {
        throw new Error('Reels need a started service for the demo guest.');
      }
      navigate(nyafazReelsPath('new'), {
        state: {
          presentationReels: true,
          checkinId: checkin?.id,
          checkin,
          guestName: checkin?.customer_name,
        },
      });
    } catch (e) {
      setError(e?.message || 'Could not open reels.');
    } finally {
      setBusyId('');
    }
  };

  const handlePassport = async () => {
    if (busyId) return;
    setBusyId('passport');
    setError('');
    try {
      await openPresentationPassportScan(navigate);
    } catch (e) {
      setError(e?.message || 'Could not open passport scan.');
    } finally {
      setBusyId('');
    }
  };

  const handleCustomerEngage = () => {
    if (design === 'nyafaz') {
      window.open(`${NYAFAZ_ENGAGE_URL}/`, '_blank', 'noopener');
    } else if (PRESENTATION_CUSTOMER_ENGAGE_URL) {
      window.open(PRESENTATION_CUSTOMER_ENGAGE_URL, '_blank', 'noopener');
    } else {
      window.open('/naturals/engagement/', '_blank', 'noopener');
    }
  };

  const handleTile = (tile) => {
    if (tile.externalUrl) {
      window.open(tile.externalUrl, '_blank', 'noopener');
      return;
    }
    if (!ready || busyId) return;
    if (tile.action === 'skin') {
      handleSkin();
      return;
    }
    if (tile.action === 'reels') {
      handleReels();
      return;
    }
    if (tile.action === 'passport') {
      handlePassport();
      return;
    }
    if (tile.path) openPath(tile.path);
  };

  const handleLogout = async () => {
    await logoutFranchiseFlow();
    navigate(PRESENTATION_LOGIN_PATH, { replace: true });
  };

  // A card title whose longest word is wider than its card (e.g. "Recommendation" on a laptop)
  // shrinks just enough to fit; every other title keeps the normal size. Cards never change size.
  const fitCardTitles = useCallback(() => {
    document.querySelectorAll('.naturals-pres-grid .naturals-pres-card-title').forEach((title) => {
      title.style.fontSize = '';
      let size = parseFloat(window.getComputedStyle(title).fontSize);
      while (title.scrollWidth > title.clientWidth + 1 && size > 8) {
        size -= 0.25;
        title.style.fontSize = `${size}px`;
      }
    });
  }, []);
  useLayoutEffect(fitCardTitles);
  useEffect(() => {
    window.addEventListener('resize', fitCardTitles);
    if (document.fonts?.ready) document.fonts.ready.then(fitCardTitles).catch(() => {});
    return () => window.removeEventListener('resize', fitCardTitles);
  }, [fitCardTitles]);

  // ── NATURALS / NYAFAZ LAYOUT (same tiles; Nyafaz swaps the skin, background and logo) ──
  if (design === 'naturals' || design === 'nyafaz') {
    const isNyafaz = design === 'nyafaz';
    const isPassportBusy = busyId === 'passport';
    const isSkinBusy = busyId === 'skin';
    const isReelsBusy = busyId === 'reels';
    const isAnyActionBusy = Boolean(busyId);
    const actionsDisabled = !ready || isAnyActionBusy;

    // Always the bundled, tightly cropped HD logo. The org logo from the backend is a 2500x2500 square with the
    // logo using under half its height, so swapping to it after load made the logo jump from big to small.
    const headerLogoSrc = `${process.env.PUBLIC_URL || ''}/engagement/naturals_nxt_logo_hd.png`;
    const nyafazLogoSrc = `${process.env.PUBLIC_URL || ''}/enterprise/logo.png`;
    // Nyafaz shows its own hero lines; Naturals keeps the kiosk landing text from the backend.
    const heroKicker = isNyafaz ? 'Beauty Intelligence Platform' : landingConfig?.subtitle || 'Welcome to';
    const heroTitle = isNyafaz ? 'Smart Salon Suite' : landingConfig?.title || 'AI Suite';
    const heroTagline = isNyafaz
      ? 'The Intelligent Operating System for Modern Salons'
      : brand.tagline || 'Your Beauty | Our Expertise';
    const naturalsHubStyle = {
      ...hubSizingStyle,
      ...(isNyafaz
        ? { '--naturals-hub-bg': NYAFAZ_HUB_BACKGROUND, '--nyafaz-hub-bg-tablet': NYAFAZ_HUB_BACKGROUND_TABLET }
        : presentationHubBackgroundStyle()),
    };

    // 15 Presentation Cards, 5 per row on desktop:
    // Row 1: Passport, Skin Analysis, Hair Analysis, Makeup, Hair Coloring
    // Row 2: Hair Style, Reels, Customer Engagement, Beauty Advisor, AI Voice Call
    // Row 3: AI WhatsApp Chat, Product Recommendation Engine, AI Stylist Trainer, Booking, POS
    const cards = [
      {
        id: 'passport',
        title: isPassportBusy ? 'Opening Digital Check-in…' : 'Digital Check-in',
        icon: <PassportCardIcon />,
        onClick: handlePassport,
        disabled: actionsDisabled,
      },
      {
        id: 'skin',
        title: isSkinBusy ? 'Starting Skin…' : 'Skin Analysis',
        icon: <SkinAnalysisIcon />,
        onClick: handleSkin,
        disabled: actionsDisabled,
      },
      {
        id: 'hair-analyze',
        title: 'Hair Analysis',
        icon: <HairAnalysisIcon />,
        onClick: async () => {
          if (actionsDisabled) return;
          setBusyId('hair-analyze');
          setError('');
          try {
            await ensurePresentationGuestCheckin();
            openPath(PRESENTATION_NATURALS_HAIR_ANALYSIS_HOME);
          } catch (e) {
            setError(e?.message || 'Could not open hair analysis.');
          } finally {
            setBusyId('');
          }
        },
        disabled: actionsDisabled,
      },
      {
        id: 'makeup',
        title: busyId === 'makeup' ? 'Opening Makeup…' : 'Makeup',
        icon: <MakeupIcon />,
        onClick: () => openBeautyStudio(PRESENTATION_NATURALS_MAKEUP_CAPTURE, 'makeup'),
        disabled: actionsDisabled,
      },
      {
        id: 'hair-coloring',
        title: busyId === 'hair-coloring' ? 'Opening Hair Coloring…' : 'Hair Coloring',
        icon: <HairColoringIcon />,
        onClick: () => openBeautyStudio(PRESENTATION_NATURALS_HAIR_COLOR_CAPTURE, 'hair-coloring'),
        disabled: actionsDisabled,
      },
      {
        id: 'hair-style',
        title: busyId === 'hair-style' ? 'Opening Hair Style…' : 'Hair Style',
        icon: <HairStyleIcon />,
        onClick: () => openBeautyStudio(PRESENTATION_NATURALS_HAIR_STYLE_CAPTURE, 'hair-style'),
        disabled: actionsDisabled,
      },
      {
        id: 'reels',
        title: isReelsBusy ? 'Opening Reels…' : 'Reels',
        icon: <ReelsCameraIcon />,
        onClick: handleReels,
        disabled: actionsDisabled,
      },
      {
        id: 'customer-engage',
        title: 'Customer Engagement',
        icon: <CustomerEngageCardIcon />,
        onClick: handleCustomerEngage,
        disabled: false,
      },
      {
        id: 'ai-voice-call',
        title: 'AI Voice Call',
        icon: <AIVoiceCallIcon />,
        onClick: () => window.open(isNyafaz ? NYAFAZ_AI_VOICE_CALL_URL : AI_VOICE_CALL_URL, '_blank', 'noopener'),
        disabled: false,
      },
      {
        id: 'ai-whatsapp',
        title: 'AI WhatsApp Chat',
        icon: <AIWhatsAppIcon />,
        onClick: () => window.open(AI_WHATSAPP_URL, '_blank', 'noopener'),
        disabled: false,
      },
      {
        id: 'product-recommender',
        title: ['Product', 'Recommendation', 'Engine'].join('\n'), // three lines (card titles keep line breaks)
        icon: <ProductRecommenderIcon />,
        onClick: () => openPath(SALON_SERVICE_LOBBY_PATH),
        disabled: actionsDisabled,
      },
      {
        id: 'booking',
        title: 'Booking',
        icon: <BookingIcon />,
        onClick: () => window.open(isNyafaz ? NYAFAZ_WALK_IN_BOOKING_URL : WALK_IN_BOOKING_URL, '_blank', 'noopener'),
        disabled: false,
      },
      {
        id: 'pos',
        title: 'POS',
        icon: <PosIcon />,
        onClick: () => window.open(DEMO_POS_URL, '_blank', 'noopener'),
        disabled: false,
      },
    ];

    return (
      <div
        ref={pageRef}
        className={`naturals-pres-hub${isNyafaz ? ' nyafaz-pres-hub' : ''}`}
        style={naturalsHubStyle}
      >
        {/* Top Header */}
        <header className="naturals-pres-header">
          <div className="naturals-pres-header-left">
            <button
              type="button"
              className="naturals-pres-home-btn"
              onClick={() => navigate(PRESENTATION_HUB_PATH, { replace: true })}
              aria-label="Home"
              title="Presentation home"
            >
              <span className="naturals-pres-home-icon-circle">
                <HomeNavIcon />
              </span>
              <span className="naturals-pres-home-label">Home</span>
            </button>
          </div>

          <div className="naturals-pres-header-right">
            {/* Nyafaz: reload the hub (fresh check-in data, settings and tiles) */}
            {isNyafaz ? (
              <button
                type="button"
                className="naturals-pres-round-btn"
                onClick={() => window.location.reload()}
                aria-label="Refresh"
                title="Refresh"
              >
                <RefreshNavIcon />
              </button>
            ) : null}

            {designSwitcher}

            {/* The Nyafaz design leaves out the accessibility button. */}
            {!isNyafaz ? (
              <button
                type="button"
                className="naturals-pres-round-btn"
                aria-label="Accessibility options"
                title="Accessibility"
              >
                <AccessibilityFigureIcon />
              </button>
            ) : null}

            <button
              type="button"
              className="naturals-pres-lang-pill"
              aria-label="Select language"
              title="Language"
            >
              <GlobeNavIcon />
              <span>EN</span>
              <span style={{ fontSize: '0.72rem', opacity: 0.8 }}>⌄</span>
            </button>

            <button
              type="button"
              className="naturals-pres-round-btn"
              onClick={handleLogout}
              aria-label="Sign out presentation kiosk"
              title="Sign out"
            >
              <SignOutNavIcon />
            </button>
          </div>
        </header>

        {/* Vertically Centered Main Body (Hero + Cards) */}
        <div className="naturals-pres-center-body">
          {/* Hero Section */}
          <section className="naturals-pres-hero">
            <div className="naturals-pres-hero-logo">
              <img
                src={isNyafaz ? nyafazLogoSrc : headerLogoSrc}
                alt={isNyafaz ? 'nyafaz' : brand.tagline || 'Naturals nxt'}
                className="naturals-brand-logo-img"
              />
            </div>
            <p className="naturals-pres-kicker">{heroKicker}</p>
            <h1 className="naturals-pres-title">{heroTitle}</h1>
            <p className="naturals-pres-tagline">
              {heroTagline.includes('|') ? (
                heroTagline.split('|').map((part, i, arr) => (
                  <span key={i}>
                    {part.trim()}
                    {i < arr.length - 1 ? (
                      <span className="naturals-pres-tagline-sep"> | </span>
                    ) : null}
                  </span>
                ))
              ) : (
                heroTagline
              )}
            </p>
          </section>

          {/* Status / Error Messages */}
          {error ? (
            <div className="naturals-pres-alert" role="alert">
              {error}
            </div>
          ) : null}

          {!ready && !error ? (
            <div className="naturals-pres-loading-banner">
              Preparing demo session…
            </div>
          ) : null}

          {/* Main Services 5-per-row Layout */}
          <main className="naturals-pres-content">
            <div className="naturals-pres-grid" role="list">
              {cards.map((card) => (
                <button
                  key={card.id}
                  type="button"
                  className={`naturals-pres-card ${card.colClass || ''}`}
                  onClick={card.onClick}
                  disabled={card.disabled}
                  role="listitem"
                >
                  <span className="naturals-pres-card-icon">
                    {card.icon}
                  </span>
                  <span className="naturals-pres-card-title">
                    {card.title}
                  </span>
                  <span className="naturals-pres-card-arrow">
                    <CircleArrowIcon />
                  </span>
                </button>
              ))}
            </div>
          </main>
        </div>

        {/* Bottom Footer: by nyafaz logo on a white pill (the Nyafaz design already shows its logo on top) */}
        {!isNyafaz ? (
          <footer className="naturals-pres-footer">
            <span className="naturals-pres-footer-by">Powered by</span>
            <span className="naturals-pres-footer-logo-badge">
              <img
                src={nyafazLogoSrc}
                alt="nyafaz"
                className="naturals-pres-footer-logo"
              />
            </span>
          </footer>
        ) : null}

        {designDialog}
      </div>
    );
  }

  // ── ORIGINAL FALLBACK LAYOUT (design === 'original') ──
  return (
    <div
      ref={pageRef}
      className="salon-flow-select-page page-bg salon-hub-has-inline-logout"
      style={{ ...hubThemeStyle, ...hubSizingStyle }}
    >
      <header className="salon-flow-select-header">
        <div className="salon-flow-select-header-actions" style={{ marginLeft: 'auto' }}>
          {designSwitcher}
          <button
            type="button"
            className="salon-flow-select-icon-btn"
            onClick={() => navigate(PRESENTATION_HUB_PATH, { replace: true })}
            aria-label="Home"
            title="Presentation home"
          >
            ⌂
          </button>
          <button
            type="button"
            className="salon-flow-select-icon-btn"
            onClick={handleLogout}
            aria-label="Sign out"
            title="Sign out"
          >
            ⎋
          </button>
        </div>
      </header>

      <main className="salon-flow-select-main">
        <div className="salon-flow-select-brand">
          {showBrandLogo ? (
            <FaceIdentityBrandLogo
              showLogo={showLogo}
              orgLogoUrl={orgLogoUrl}
              onLogoError={onLogoError}
              imgClassName="salon-flow-select-logo"
              fallbackClassName="salon-flow-select-logo-text"
            />
          ) : null}
        </div>

        <h1 className="salon-flow-select-title">Services</h1>

        {error ? (
          <p className="salon-flow-select-subtitle salon-flow-select-error" role="alert">
            {error}
          </p>
        ) : null}

        {!ready && !error ? (
          <p className="salon-flow-select-subtitle">Preparing demo session…</p>
        ) : null}

        <div className="salon-flow-select-grid" role="list">
          {LEGACY_TILES.map((tile) => {
            const isBusy = busyId === tile.id || busyId === tile.action;
            const disabled = tile.externalUrl ? false : !ready || Boolean(busyId);
            return (
              <button
                key={tile.id}
                type="button"
                className={`salon-flow-select-card salon-flow-select-card--simple${disabled ? ' is-disabled' : ''}`}
                onClick={() => handleTile(tile)}
                disabled={disabled}
                role="listitem"
              >
                <span className="salon-flow-select-card-icon" aria-hidden="true">
                  {tile.iconSrc ? (
                    <img
                      src={tile.iconSrc}
                      alt=""
                      className="salon-flow-select-card-icon-img"
                      loading="lazy"
                      decoding="async"
                    />
                  ) : tile.icon === 'passport-svg' ? (
                    <LegacyPassportIcon />
                  ) : tile.icon === 'engage-svg' ? (
                    <LegacyCustomerEngageIcon />
                  ) : (
                    <LegacyReelsIcon />
                  )}
                </span>
                <span className="salon-flow-select-card-title">
                  {isBusy ? 'Opening…' : tile.title}
                </span>
              </button>
            );
          })}
        </div>
      </main>

      {designDialog}
    </div>
  );
}
